import { useMemo, useEffect, useLayoutEffect, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { size, compact } from "lodash"
import { Paper } from "@mui/material"
import { Creators as NotificationCreators } from "modules/notifications/model"
import Error from "components/error"
import Loading from "components/loading"
import NoData from "components/intraday/no-data"
import { usePositionSetMetadata } from "resources/position-sets"
import { useModelMetadata } from "resources/models"
import { useDisplayCurrency } from "resources/currencies"
import { getIsFxHedgeSupportedByModel } from "connected-components/compare/position-sets"
import { useContentSetsMetadata } from "resources/content-sets"
import { useActiveInstruments } from "resources/instruments"
import { useIntradayChannel, useLastIntradayBatch } from "hooks/intraday"
import { combineStatuses, OK, LOADING, ERROR } from "helpers/data-statuses"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { getIsIntradayFeatureEnabled, getIsIntradayAnalyticsEnabled } from "selectors/features"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "./context/dashboard-id"
import { DEFAULT_CORE_STATE } from "./store/core-slice"
import { useCoreActions } from "./hooks/use-core-actions"
import EmptyDashboard from "./components/empty"
import { useInstrumentsWithMetadata } from "./helpers"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "./analytics"
import { DashboardAnalyticsProvider } from "./analytics/provider"
import { useDefaultSelectedBase } from "./hooks/use-selected-base"
import { useModelAccessFallback } from "./hooks/use-model-access-fallback"
import useOpenUnifiedSearch from "./hooks/use-open-unified-search"
import Dashboard from "./dashboard"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import "./style.scss"

function DashboardWrapper() {
  const dispatch = useDispatch()
  const analytics = useAnalytics()
  const dashboardId = useDashboardId()

  const core = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const {
    instrument: original,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    base,
    isPreferencesLoaded,
    isIntraday,
    isEndVsStartDateComparison,
  } = core
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)

  const {
    setBase,
    setIsIntraday,
    setInstrument,
    setInstrumentsMetadata,
  } = useCoreActions()

  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const [instrumentMetadata, instrumentStatus] = usePositionSetMetadata(modelId, original, modelMetadata?.availability?.currentDate)
  const [comparisonInstrumentMetadata, comparisonInstrumentStatus] = usePositionSetMetadata(modelId, comparison, modelMetadata?.availability?.currentDate)
  const [baseInstrumentMetadata, baseInstrumentStatus] = usePositionSetMetadata(modelId, base, modelMetadata?.availability?.currentDate)
  // Currency comes from the main instrument, applied to the comparison.
  const [currency] = useDisplayCurrency(modelId, original)
  const isFxHedgeSupported = getIsFxHedgeSupportedByModel(modelMetadata?.factors, currency)
  const [instrumentsWithMetadata, instrumentsWithMetadataStatus] = useInstrumentsWithMetadata(modelId, modelMetadata?.availability?.currentDate, { original, comparison, rebalanced, base }, isFxHedgeSupported)
  const [contentSets, contentSetsStatus] = useContentSetsMetadata()

  // The selected model may not be accessible for the viewed account (a superuser sees a broader
  // model list; its metadata then comes back null). Fall back to an accessible model with a warning.
  useModelAccessFallback(modelId, modelMetadata, modelMetadataStatus, original, isPreferencesLoaded)

  const hasMultipleInstruments = useMemo(() => {
    if (isEndVsStartDateComparison) {
      return size(compact([original, rebalanced])) > 1
    }
    return size(compact([original, rebalanced, comparison])) > 1
  }, [original, rebalanced, comparison, isEndVsStartDateComparison])

  const [activeInstruments] = useActiveInstruments(modelId)
  const defaultSelectedBase = useDefaultSelectedBase(modelId)

  const status = useMemo(() =>
    combineStatuses([instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus]),
  [instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus])

  useEffect(() => {
    if (isPreferencesLoaded && modelMetadata && base && activeInstruments && !makeFindAndGetBaseMetadata(null, activeInstruments)(base)) {
      if (defaultSelectedBase && makeFindAndGetBaseMetadata(null, activeInstruments)(defaultSelectedBase)) {
        setBase(
          { id: defaultSelectedBase.id, type: defaultSelectedBase.type },
          instrumentMetadata,
        )
        dispatch(NotificationCreators.add("warning", null, "A previously saved benchmark is no longer available; this has been replaced by a default benchmark."))
      } else {
        setBase(null)
        dispatch(NotificationCreators.add("warning", null, "The selected benchmark is no longer available."))
      }
    }
  }, [isPreferencesLoaded, setBase, base, activeInstruments, defaultSelectedBase, dispatch, modelMetadata, instrumentMetadata])

  useEffect(() => {
    setInstrumentsMetadata({
      original: instrumentMetadata,
      comparison: comparisonInstrumentMetadata,
      rebalanced: instrumentsWithMetadata?.rebalanced,
      base: baseInstrumentMetadata,
    })
  }, [instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, instrumentsWithMetadata?.rebalanced, setInstrumentsMetadata])

  const isIntradayFeatureEnabled = useSelector(getIsIntradayFeatureEnabled)
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)

  useLayoutEffect(() => {
    const isIntradayByInstrument = isIntradayFeatureEnabled && instrumentMetadata?.intradayEnabled
    setIsIntraday(!!isIntradayByInstrument || !!isIntradayAnalyticsEnabled)
  }, [isIntraday, instrumentMetadata, setIsIntraday, isIntradayFeatureEnabled, isIntradayAnalyticsEnabled])

  useIntradayChannel(isIntraday, original?.id, original?.type, "dashboard")
  const [lastIntradayBatch, lastIntradayBatchStatus] = useLastIntradayBatch(original, instrumentMetadata?.intradayEnabled)

  const onMainInstrumentSelection = useCallback(({ id, name, type, searchType, isInitialSelection }) => {
    const payload = { instrument: { id, name, type }, searchType }
    if (isInitialSelection) {
      analytics.track(DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION, payload)
    } else {
      analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, payload)
    }
  }, [analytics])

  const openMainInstrumentSearch = useOpenUnifiedSearch(setInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, null, false, false, onMainInstrumentSelection)

  if (status === LOADING) {
    return <Loading />
  }

  if (status === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, { type: "data_loading_error", message: "Failed to load dashboard data" })
    return <Error />
  }

  if (!isPreferencesLoaded || status !== OK) {
    return <Loading />
  }

  if (!original) {
    return <EmptyDashboard openUnifiedSearch={openMainInstrumentSearch} />
  }

  if (!instrumentMetadata) {
    return <Loading />
  }

  if (isIntraday && instrumentMetadata?.intradayMetadata && !lastIntradayBatch) {
    return <Loading />
  } else if (isIntraday && instrumentMetadata?.intradayMetadata && !lastIntradayBatch?.id) {
    return <Paper className="details"><NoData autoRefresh /></Paper>
  }

  return (
    <Dashboard
      modelMetadata={modelMetadata}
      instrumentMetadata={instrumentMetadata}
      comparisonInstrumentMetadata={comparisonInstrumentMetadata}
      baseInstrumentMetadata={baseInstrumentMetadata}
      instrumentsWithMetadata={instrumentsWithMetadata}
      contentSets={contentSets}
      hasMultipleInstruments={hasMultipleInstruments}
      lastIntradayBatch={lastIntradayBatch}
      lastIntradayBatchStatus={lastIntradayBatchStatus}
    />
  )
}

export default function DashboardWrapperWithAnalytics() {
  return (
    <DashboardAnalyticsProvider>
      <DashboardWrapper />
    </DashboardAnalyticsProvider>
  )
}
